import { InstrumentationBase } from '@opentelemetry/instrumentation';
import { context, propagation, trace, SpanKind, SpanStatusCode } from '@opentelemetry/api';
function urlMatches(url, patterns) {
  return patterns.some(pattern => {
    if (typeof pattern === 'string') {
      return url.includes(pattern);
    }
    pattern.lastIndex = 0;
    return pattern.test(url);
  });
}
function getStringByteLength(str) {
  let byteLength = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code <= 0x7f) byteLength += 1;else if (code <= 0x7ff) byteLength += 2;else if (code >= 0xd800 && code <= 0xdbff) {
      byteLength += 4;
      i++;
    } else byteLength += 3;
  }
  return byteLength;
}
function getMessageSize(data) {
  if (typeof data === 'string') return getStringByteLength(data);
  if (data instanceof ArrayBuffer) return data.byteLength;
  if (data instanceof Blob) return data.size;
  if (ArrayBuffer.isView(data)) return data.byteLength;
  return 0;
}
function getMessageType(data) {
  return typeof data === 'string' ? 'text' : 'binary';
}
let connectionCounter = 0;
function generateConnectionId() {
  return `ws_${Date.now()}_${++connectionCounter}`;
}
const NORMAL_CLOSE_CODES = new Set([1000, 1001]);
function isAbnormalCloseCode(code) {
  return !NORMAL_CLOSE_CODES.has(code);
}
export class WebSocketInstrumentation extends InstrumentationBase {
  constructor(config = {}) {
    super('opentelemetry-fe-websocket', '0.1.0', config);
    this._originalWebSocket = null;
  }
  init() {}
  enable() {
    if (typeof globalThis.WebSocket === 'undefined') return;
    if (this._originalWebSocket !== null) return;
    this._originalWebSocket = globalThis.WebSocket;
    const OriginalWebSocket = this._originalWebSocket;
    const getConfig = () => this.getConfig();
    const getTracer = () => this.tracer;
    const instrumentInstance = (ws, url, connectionSpan) => this._instrumentInstance(ws, url, connectionSpan);
    function PatchedWebSocket(url, protocols) {
      var _config$ignoreUrls, _config$propagateTrac;
      const urlString = typeof url === 'string' ? url : url.toString();
      const config = getConfig();
      if (urlMatches(urlString, (_config$ignoreUrls = config.ignoreUrls) !== null && _config$ignoreUrls !== void 0 ? _config$ignoreUrls : [])) {
        return new OriginalWebSocket(url, protocols);
      }
      const connectionSpan = getTracer().startSpan('ws.connection', {
        kind: SpanKind.CLIENT
      });
      let constructUrl = url;
      if (urlMatches(urlString, (_config$propagateTrac = config.propagateTraceHeaderWebSocketUrls) !== null && _config$propagateTrac !== void 0 ? _config$propagateTrac : [])) {
        const carrier = {};
        propagation.inject(trace.setSpan(context.active(), connectionSpan), carrier);
        const traceparent = carrier['traceparent'];
        if (traceparent) {
          const separator = urlString.includes('?') ? '&' : '?';
          constructUrl = `${urlString}${separator}traceparent=${encodeURIComponent(traceparent)}`;
        }
      }
      let instance;
      try {
        instance = new OriginalWebSocket(constructUrl, protocols);
      } catch (err) {
        connectionSpan.recordException(err instanceof Error ? err : new Error(String(err)));
        connectionSpan.setStatus({
          code: SpanStatusCode.ERROR
        });
        connectionSpan.end();
        throw err;
      }
      instrumentInstance(instance, urlString, connectionSpan);
      return instance;
    }
    PatchedWebSocket.prototype = OriginalWebSocket.prototype;
    PatchedWebSocket.CONNECTING = OriginalWebSocket.CONNECTING;
    PatchedWebSocket.OPEN = OriginalWebSocket.OPEN;
    PatchedWebSocket.CLOSING = OriginalWebSocket.CLOSING;
    PatchedWebSocket.CLOSED = OriginalWebSocket.CLOSED;
    Object.setPrototypeOf(PatchedWebSocket, OriginalWebSocket);
    globalThis.WebSocket = PatchedWebSocket;
  }
  disable() {
    if (this._originalWebSocket !== null) {
      globalThis.WebSocket = this._originalWebSocket;
      this._originalWebSocket = null;
    }
  }
  _instrumentInstance(ws, url, connectionSpan) {
    const tracer = this.tracer;
    let hadError = false;
    let connectionEnded = false;
    const connectionId = generateConnectionId();
    const sharedAttributes = {
      'websocket.url': url,
      'websocket.connection_id': connectionId
    };
    connectionSpan.setAttributes(sharedAttributes);
    let unloadHandler = null;
    const endConnectionSpan = (code, reason, status) => {
      if (connectionEnded) return;
      connectionEnded = true;
      connectionSpan.setAttributes({
        'websocket.close_code': code,
        'websocket.close_reason': reason
      });
      connectionSpan.setStatus({
        code: status
      });
      connectionSpan.end();
      if (unloadHandler !== null) {
        window.removeEventListener('pagehide', unloadHandler);
        unloadHandler = null;
      }
    };
    unloadHandler = () => {
      endConnectionSpan(1001, 'page unload', SpanStatusCode.OK);
    };
    window.addEventListener('pagehide', unloadHandler);
    ws.addEventListener('open', () => {
      connectionSpan.addEvent('open');
    });
    ws.addEventListener('error', () => {
      hadError = true;
    });
    ws.addEventListener('close', event => {
      endConnectionSpan(event.code, event.reason, hadError || isAbnormalCloseCode(event.code) ? SpanStatusCode.ERROR : SpanStatusCode.OK);
    });
    ws.addEventListener('message', event => {
      const span = tracer.startSpan('ws.receive', {
        kind: SpanKind.CONSUMER,
        attributes: Object.assign({}, sharedAttributes, {
          'websocket.message.size': getMessageSize(event.data),
          'websocket.message.type': getMessageType(event.data)
        })
      });
      span.setStatus({
        code: SpanStatusCode.OK
      });
      span.end();
    });
    const originalSend = ws.send.bind(ws);
    ws.send = data => {
      const span = tracer.startSpan('ws.send', {
        kind: SpanKind.CLIENT,
        attributes: Object.assign({}, sharedAttributes, {
          'websocket.message.size': getMessageSize(data),
          'websocket.message.type': getMessageType(data)
        })
      });
      try {
        originalSend(data);
        span.setStatus({
          code: SpanStatusCode.OK
        });
      } catch (err) {
        span.recordException(err instanceof Error ? err : new Error(String(err)));
        span.setStatus({
          code: SpanStatusCode.ERROR
        });
        throw err;
      } finally {
        span.end();
      }
    };
  }
}